import { NotifyMeButton } from "@/components/waitlist/NotifyMeButton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { applications } from "@/data/applications";
import { ExternalLink, Settings, Trash2, RefreshCw } from "lucide-react";

interface AppManageDialogProps {
  appName: string;
  onClose: () => void;
}

export function AppManageDialog({ appName, onClose }: AppManageDialogProps) {
  
  const app = applications.find(a => a.name === appName);

  if (!app) return null;

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10">
              <app.icon className="h-6 w-6 text-primary" />
            </div>
            <div>
              <DialogTitle>{app.name}</DialogTitle>
              <DialogDescription>{app.description}</DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-6">
          {/* Status Card */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center justify-between">
                État de la connexion
                <Badge 
                  variant="secondary" 
                  
                >
                  Bientôt disponible
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-4">
                Bientôt disponible.
              </p>
              <NotifyMeButton channel={appName} className="mb-4" />
              <div className="flex gap-2">
                <Button variant="outline" size="sm" disabled>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Bientôt disponible
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  disabled
                >
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Ouvrir l'application
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Features */}
          {app.features && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Fonctionnalités</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {app.features.map((feature, index) => (
                    <li key={index} className="flex items-center gap-2 text-sm">
                      <div className="h-1.5 w-1.5 rounded-full bg-primary flex-shrink-0" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          {/* Pricing */}
          {app.price && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Tarification</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span>Plan mensuel</span>
                    <span className="font-semibold">${app.price.monthly}/mois</span>
                  </div>
                  {app.price.annual && (
                    <div className="flex justify-between items-center">
                      <span>Plan annuel</span>
                      <span className="font-semibold">${app.price.annual}/an</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Actions */}
          <div className="flex justify-between pt-4">
            <Button variant="outline" onClick={onClose}>
              Fermer
            </Button>
            <Button 
              variant="destructive" 
              disabled
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Désinstaller
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}